import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SummaryApi from "../common";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

const MonthlyOrders = () => {
  const [orderData, setOrderData] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  // Fetch orders
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(SummaryApi.allOrder.url, {
        method: SummaryApi.allOrder.method,
        headers: {
        Authorization: SummaryApi.bearerToken,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch orders");

      const data = await response.json();
      // Ensure data.data is an array before setting state
      setOrderData(Array.isArray(data.data) ? data.data.reverse() : []);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter orders
  const filteredOrders = orderData.filter((o) => {
    const orderDate = new Date(o.order_date);
    let matchesDate = true;
    if (fromDate && toDate) {
      // Add 1 day to 'toDate' to include the full day
      const toEnd = new Date(toDate.getTime() + 86400000);
      matchesDate = orderDate >= fromDate && orderDate <= toEnd;
    }
    let matchesSearch = true;
    if (searchTerm) {
      matchesSearch = o.order_code?.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return matchesDate && matchesSearch;
  });

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    setSearchTerm("");
  };

  const downloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      filteredOrders.map((o) => ({
        "Order Code": o.order_code,
        "Customer Name": o.customer_name,
        Mobile: o.mobile_number,
        "Order Amount": o.order_amount,
        "Delivery Charges": o.delivery_charges,
        "Total Amount": o.total_amount,
        "Order Date": new Date(o.order_date).toLocaleDateString("en-IN"),
        Status: o.order_status,
        Address: o.order_address,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(wb, "MonthlyOrders.xlsx");
  };

   const handleEdit = (order) => {
    // This constructs the full path: /admin-panel/orders/edit/123
    // We also pass 'state' so the data is available immediately without re-fetching
    navigate(`/admin-panel/orders/edit/${order.order_id}`, { state: { orderData: order } });
  };

  return (
    <div className="bg-white p-4 md:p-6 rounded-lg shadow-lg border">
      <h2 className="text-lg md:text-xl font-semibold mb-4 text-gray-700">Monthly Orders</h2>

      {/* Filters */}
      <div className="flex flex-col md:flex-row mb-4 gap-2">
        <DatePicker
          selected={fromDate}
          onChange={setFromDate}
          placeholderText="From Date"
          className="border p-2 rounded w-full md:w-auto"
          minDate={startOfMonth}
          maxDate={endOfMonth}
        />
        <DatePicker
          selected={toDate}
          onChange={setToDate}
          placeholderText="To Date"
          className="border p-2 rounded w-full md:w-auto"
          minDate={startOfMonth}
          maxDate={endOfMonth}
        />
        <input
          type="text"
          placeholder="Search Order Code"
          className="border p-2 rounded flex-grow"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="flex gap-2">
            <button onClick={handleClear} className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400">Clear</button>
            <button onClick={downloadExcel} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">Excel</button>
        </div>
      </div>

      {/* Standard HTML Table */}
      {loading ? (
        <p className="text-center py-4">Loading...</p>
      ) : error ? (
        <p className="text-center text-red-500">{error}</p>
      ) : filteredOrders.length === 0 ? (
        <p className="text-center text-gray-500 py-4">No orders found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-500 border-collapse border border-gray-200">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100">
              <tr>
                <th className="px-4 py-3 border">Order Code</th>
                <th className="px-4 py-3 border">Customer Name</th>
                <th className="px-4 py-3 border">Mobile</th>
                <th className="px-4 py-3 border">Order Amount</th>
                <th className="px-4 py-3 border">Delivery</th>
                <th className="px-4 py-3 border">Total</th>
                <th className="px-4 py-3 border">Order Date</th>
                <th className="px-4 py-3 border">Status</th>
                <th className="px-4 py-3 border">Address</th>
                <th className="px-4 py-3 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order, index) => (
                <tr key={order.order_id || index} className="bg-white border-b hover:bg-gray-50">
                  <td className="px-4 py-3 border font-medium text-gray-900">{order.order_code}</td>
                  <td className="px-4 py-3 border">{order.customer_name}</td>
                  <td className="px-4 py-3 border">{order.mobile_number}</td>
                  <td className="px-4 py-3 border">₹{order.order_amount}</td>
                  <td className="px-4 py-3 border">₹{order.delivery_charges}</td>
                  <td className="px-4 py-3 border font-semibold text-black">₹{order.total_amount}</td>
                  <td className="px-4 py-3 border">
                    {new Date(order.order_date).toLocaleDateString("en-IN")}
                  </td>
                  <td className="px-4 py-3 border">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold 
                      ${order.order_status === 'Delivered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {order.order_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 border">
                    <div className="line-clamp-2 w-48" title={order.order_address}>
                      {order.order_address}
                    </div>
                  </td>
                  <td className="px-4 py-3 border">
                    <button
                      onClick={() => handleEdit(order.order_id)}
                      className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MonthlyOrders;